/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.recommendationsUpdateTimeout = null;
      
      // Find the correct overlay for this drawer
      this.overlay = document.querySelector('.CD_overlay_drawer') || document.querySelector('.js-overlay');
      
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('cart-recommendations:update', this.cartRecommendationsUpdateHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      // Remove overlay listener
      const overlay = document.querySelector('.CD_overlay_drawer');
      if (overlay && this.overlayClickHandler) {
        overlay.removeEventListener('click', this.overlayClickHandler);
      }

      // Remove escape key listener
      if (this.escapeKeyHandler) {
        document.removeEventListener('keydown', this.escapeKeyHandler);
      }

      // Clean up mutation observer
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }

      // Clean up timeout
      if (this.recommendationsUpdateTimeout) {
        clearTimeout(this.recommendationsUpdateTimeout);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
      
      // Listen for cart recommendations update events
      this.cartRecommendationsUpdateHandler = this.handleCartRecommendationsUpdate.bind(this);
      document.addEventListener('cart-recommendations:update', this.cartRecommendationsUpdateHandler);

      // Specific listener for the close button
      this.addEventListener('click', (event) => {
        if (event.target.matches('.CD_close_drawer') || event.target.closest('.CD_close_drawer')) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      });

      // Specific listener for the overlay
      const overlay = document.querySelector('.CD_overlay_drawer');
      if (overlay) {
        this.overlayClickHandler = this.close.bind(this);
        overlay.addEventListener('click', this.overlayClickHandler);
      }

      // Listen for Escape key to close drawer
      this.escapeKeyHandler = (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          event.preventDefault();
          this.close();
        }
      };
      document.addEventListener('keydown', this.escapeKeyHandler);

      // Enhanced discount handling with single debounced update
      this.addEventListener('click', (event) => {
        if (event.target.matches('#discountSubmit')) {
          // Store current product ID before discount application
          const currentProductId = this.getLastAddedProductId();
          
          // Single debounced update instead of multiple timeouts
          this.debouncedUpdateRecommendations(currentProductId);
        }
      });

      // Listen for cart update events that might be triggered by discount applications
      document.addEventListener('on:cart:update', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for cart refresh events
      document.addEventListener('on:cart:refresh', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for any cart-related events
      document.addEventListener('on:cart:change', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for discount application events
      document.addEventListener('discount:applied', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for discount removal events
      document.addEventListener('discount:removed', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for cart items refresh events
      document.addEventListener('cart-items:refresh', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for cart drawer open events to ensure recommendations are visible
      document.addEventListener('cart-drawer:opened', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for cart drawer content updates
      document.addEventListener('cart-drawer:content-updated', (event) => {
        const currentProductId = this.getLastAddedProductId();
        this.debouncedUpdateRecommendations(currentProductId);
      });

      // Listen for price updates only (without affecting recommendations)
      document.addEventListener('cart-price:update', (event) => {
        // Update only price-related sections without touching recommendations
        this.updatePriceSectionsOnly(event.detail.cartData);
      });

      // Listen for any DOM changes in the cart drawer
      const observer = new MutationObserver((mutations) => {
        let shouldUpdateRecommendations = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // Check if recommendations were removed or cart items changed
            const recommendations = this.querySelector('product-recommendations');
            if (!recommendations || recommendations.innerHTML.trim() === '') {
              shouldUpdateRecommendations = true;
            }
            
            // Check if cart items were updated
            if (mutation.target.closest('.cart-item') || mutation.target.closest('cart-items')) {
              shouldUpdateRecommendations = true;
            }
          }
        });
        
        if (shouldUpdateRecommendations) {
          const currentProductId = this.getLastAddedProductId();
          this.debouncedUpdateRecommendations(currentProductId);
        }
      });
      
      // Start observing the cart drawer for changes
      observer.observe(this, {
        childList: true,
        subtree: true
      });
      
      // Store observer for cleanup
      this.mutationObserver = observer;
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Handle cart recommendations update events
     * @param {object} evt - Event object.
     */
    handleCartRecommendationsUpdate(evt) {
      if (this.hasAttribute('open')) {
        const { productId } = evt.detail;
        console.log('📢 Cart recommendations update event received for product:', productId);
        
        setTimeout(() => {
          const recommendations = this.querySelector('product-recommendations');
          if (recommendations) {
            recommendations.dataset.productId = productId.toString();
            recommendations.init();
            console.log('🔄 Updated cart recommendations from event');
          }
        }, 1500); // Wait a bit longer for cart to fully update
      }
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      this.setAttribute('aria-modal', 'true');

      if (elementToFocus) {
        elementToFocus.focus();
      } else if (this.querySelector('.drawer__header')) {
        this.querySelector('.drawer__header').focus();
      }

      this.addEventListener('transitionend', () => {
        this.querySelector('.drawer__content').focus();
      }, { once: true });

      // Dispatch event for cart drawer opened
      document.dispatchEvent(new CustomEvent('cart-drawer:opened', {
        bubbles: true,
        detail: { timestamp: Date.now() }
      }));

      // Initialize recommendations when drawer opens
      setTimeout(() => {
        this.initializeRecommendations();
      }, 100);
    }

    /**
     * Initialize recommendations based on cart contents
     */
    initializeRecommendations() {
      // Remove any existing debug buttons
      this.removeDebugButtons();
      
      const recommendations = this.querySelector('product-recommendations');
      if (!recommendations) {
        return;
      }

      // Check if cart is empty and clear localStorage if needed
      this.checkAndClearEmptyCart();

      // Get the last added product ID
      let productId = this.getLastAddedProductId();
      
      if (productId) {
        recommendations.dataset.productId = productId;
        recommendations.init();
        
        // Listen for product additions from recommendations
        this.setupRecommendationsListener();
      }
    }

    /**
     * Remove any debug buttons that might exist
     */
    removeDebugButtons() {
      const debugButtons = this.querySelectorAll('.debug-recommendations-btn');
      debugButtons.forEach(button => button.remove());
      
      // Also check for any debug buttons in the document
      const globalDebugButtons = document.querySelectorAll('.debug-recommendations-btn');
      globalDebugButtons.forEach(button => button.remove());
    }

    /**
     * Check if cart is empty and clear localStorage if needed
     */
    checkAndClearEmptyCart() {
      const cartItems = this.querySelectorAll('.cart-item');
      if (cartItems.length === 0) {
        localStorage.removeItem('lastAddedProductId');
      }
    }

    /**
     * Setup listener for product additions from recommendations
     */
    setupRecommendationsListener() {
      // Listen for cart:add events from recommendations
      this.addEventListener('on:cart:add', (event) => {
        const variantId = event.detail.variantId;
        
        // Update localStorage with the new product
        setTimeout(() => {
          this.updateLastAddedProductFromCart();
        }, 300);
      });

      // Listen for line-item:change events (quantity changes, removals)
      this.addEventListener('on:line-item:change', (event) => {
        setTimeout(() => {
          this.updateLastAddedProductFromCart();
        }, 500);
      });

      // Listen for remove button clicks specifically
      this.addEventListener('click', (event) => {
        if (event.target.matches('.js-remove-item')) {
          // Wait longer for the removal to complete
          setTimeout(() => {
            this.updateLastAddedProductFromCart();
          }, 1000);
        }
      });

      // Listen for discount events
      this.addEventListener('click', (event) => {
        if (event.target.matches('#discountSubmit') || event.target.matches('.js-remove-discount')) {
          // Wait for discount to be applied/removed
          setTimeout(() => {
            this.updateLastAddedProductFromCart();
          }, 1000);
        }
      });

      // Listen for discount form submission
      const discountForm = this.querySelector('#discountForm');
      if (discountForm) {
        discountForm.addEventListener('submit', (event) => {
          event.preventDefault();
          setTimeout(() => {
            this.updateLastAddedProductFromCart();
          }, 1000);
        });
      }
    }

    /**
     * Update the last added product ID from current cart contents
     */
    updateLastAddedProductFromCart() {
      const cartItems = this.querySelectorAll('.cart-item');
      
      if (cartItems.length === 0) {
        // Cart is empty, clear localStorage
        localStorage.removeItem('lastAddedProductId');
        
        // Remove recommendations
        const recommendations = this.querySelector('product-recommendations');
        if (recommendations) {
          recommendations.innerHTML = '';
        }
        return;
      }
      
      // Use the first product in cart as the last added
      const firstCartItem = cartItems[0];
      const productId = firstCartItem.dataset.productId;
      
      localStorage.setItem('lastAddedProductId', productId);
      
      // Update recommendations
      this.updateRecommendationsImmediately(productId);
    }

    /**
     * Update recommendations immediately with retry mechanism
     */
    updateRecommendationsImmediately(productId) {
      const recommendations = this.querySelector('product-recommendations');
      
      // Check if already loading or already correct
      if (recommendations && recommendations.classList.contains('loading')) {
        return;
      }
      
      if (recommendations && recommendations.dataset.productId === productId && 
          recommendations.innerHTML.trim() !== '') {
        return;
      }
      
      if (recommendations && typeof recommendations.init === 'function') {
        recommendations.classList.add('loading');
        recommendations.dataset.productId = productId;
        recommendations.init();
        
        // Remove loading class after a short delay
        setTimeout(() => {
          recommendations.classList.remove('loading');
        }, 500);
      } else {
        setTimeout(() => {
          this.retryUpdateRecommendations(productId);
        }, 200);
      }
    }

    /**
     * Retry updating recommendations
     */
    retryUpdateRecommendations(productId) {
      const recommendations = this.querySelector('product-recommendations');
      if (recommendations && typeof recommendations.init === 'function') {
        // Check if already loading or already correct
        if (recommendations.classList.contains('loading')) {
          return;
        }
        
        if (recommendations.dataset.productId === productId && 
            recommendations.innerHTML.trim() !== '') {
          return;
        }
        
        recommendations.classList.add('loading');
        recommendations.dataset.productId = productId;
        recommendations.init();
        
        // Remove loading class after a short delay
        setTimeout(() => {
          recommendations.classList.remove('loading');
        }, 500);
      }
    }

    /**
     * Get the last added product ID from localStorage or determine from cart
     */
    getLastAddedProductId() {
      // Get the last added product ID from localStorage
      let productId = localStorage.getItem('lastAddedProductId');
      
      if (productId) {
        // Verify that this product is still in the cart
        const cartItems = this.querySelectorAll('.cart-item');
        const productExistsInCart = Array.from(cartItems).some(item => 
          item.dataset.productId === productId
        );
        
        if (productExistsInCart) {
          return productId;
        } else {
          localStorage.removeItem('lastAddedProductId');
          productId = null;
        }
      }
      
      // If no localStorage or product not in cart, get the first product from the cart as fallback
      const cartItems = this.querySelectorAll('.cart-item');
      if (cartItems.length > 0) {
        const firstCartItem = cartItems[0];
        productId = firstCartItem.dataset.productId;
        
        // Update localStorage with this product ID
        localStorage.setItem('lastAddedProductId', productId);
        
        return productId;
      }
      
      return null;
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      // Update recommendations after rendering contents
      setTimeout(() => {
        this.updateLastAddedProductFromCart();
      }, 100);

      // Dispatch event for cart drawer content update
      document.dispatchEvent(new CustomEvent('cart-drawer:content-updated', {
        bubbles: true,
        detail: { timestamp: Date.now() }
      }));

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }

    /**
     * Debounced function to update recommendations
     * @param {string} productId - The product ID to use for recommendations
     */
    debouncedUpdateRecommendations(productId) {
      // Clear any existing timeout
      if (this.recommendationsUpdateTimeout) {
        clearTimeout(this.recommendationsUpdateTimeout);
      }
      
      // Set new timeout
      this.recommendationsUpdateTimeout = setTimeout(() => {
        this.ensureRecommendationsPersist(productId);
        this.recommendationsUpdateTimeout = null;
      }, 300); // 300ms debounce delay
    }

    /**
     * Ensure recommendations persist after discount application or cart updates
     * @param {string} productId - The product ID to use for recommendations
     */
    ensureRecommendationsPersist(productId) {
      if (!productId) {
        // If no product ID, try to get it from cart
        this.updateLastAddedProductFromCart();
        return;
      }

      const recommendations = this.querySelector('product-recommendations');
      
      if (!recommendations) {
        // If recommendations element doesn't exist, try to initialize it
        setTimeout(() => {
          this.initializeRecommendations();
        }, 100);
        return;
      }

      // Check if recommendations are already correctly configured and not empty
      if (recommendations.dataset.productId === productId && 
          recommendations.innerHTML.trim() !== '' &&
          !recommendations.classList.contains('loading') &&
          recommendations.querySelector('.slider__item')) { // Check if there are actual products
        // Recommendations are already correct and have content, don't update
        return;
      }

      // Add loading class to prevent multiple updates
      recommendations.classList.add('loading');

      // Check if recommendations are empty or need to be updated
      if (recommendations.innerHTML.trim() === '' || 
          recommendations.dataset.productId !== productId ||
          !recommendations.querySelector('.slider__item')) {
        
        if (typeof recommendations.init === 'function') {
          recommendations.dataset.productId = productId;
          recommendations.init();
          
          // Remove loading class after a short delay
          setTimeout(() => {
            recommendations.classList.remove('loading');
          }, 500);
        } else {
          // If init method doesn't exist, try to reinitialize the component
          setTimeout(() => {
            this.retryUpdateRecommendations(productId);
            recommendations.classList.remove('loading');
          }, 200);
        }
      } else {
        // Remove loading class if no update was needed
        recommendations.classList.remove('loading');
      }
    }

    /**
     * Update price-related sections only without affecting recommendations
     * @param {object} cartData - Cart data from Shopify
     */
    updatePriceSectionsOnly(cartData) {
      try {
        // Update cart summary section (which includes the checkout button)
        const cartSummary = this.querySelector('.cart-drawer__summary');
        if (cartSummary) {
          // Get the sections that need to be updated
          const sections = [
            {
              id: 'cart-drawer',
              section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
              selector: '.cart-drawer__summary'
            }
          ];
          
          // Fetch updated sections from Shopify
          const sectionNames = sections.map(s => s.section);
          fetch(`?sections=${sectionNames.join(',')}`)
            .then(response => response.json())
            .then(data => {
              sections.forEach(section => {
                const sectionEl = document.getElementById(section.id);
                if (sectionEl) {
                  const el = sectionEl.querySelector(section.selector) || sectionEl;
                  el.innerHTML = CartDrawer.getElementHTML(
                    data[section.section],
                    section.selector
                  );
                }
              });
              
              console.log('✅ Secciones de precio actualizadas sin afectar recomendaciones');
            })
            .catch(error => {
              console.error('❌ Error al actualizar secciones de precio:', error);
            });
        }
      } catch (error) {
        console.error('❌ Error en updatePriceSectionsOnly:', error);
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
